/*
 * Spruce custom cart drawer (snippets/spruce-cart-drawer.liquid).
 *
 * Keeps Dawn's <cart-drawer> API (open, close, renderContents, getSectionsToRender,
 * setActiveElement) so product-form.js / quick-add keep working.
 *
 * Free gift sync: the Liquid snippet renders the tier state into [data-spc-state].
 * After every render we compare the gift lines in the cart (property `_spruce_gift` = slot)
 * with the number of gifts earned, then add / remove gift lines so they match.
 */
(() => {
  if (customElements.get('cart-drawer')) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SECTIONS = ['spruce-cart-drawer', 'cart-icon-bubble'];
  const CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const nativeFetch = window.fetch.bind(window);

  const postJSON = (path, body) =>
    nativeFetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(data.description || data.message || 'Cart error'), { data });
      return data;
    });

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.refreshTimer = null;
      this.queue = Promise.resolve();

      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.setHeaderCartIconAccessibility();
      this.interceptCartRequests();

      // Fix gift lines on page load if the cart was changed somewhere else (e.g. /cart page).
      this.syncGifts().then((changed) => changed && this.refresh());
    }

    /* ---------- Dawn-compatible API ---------- */

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    getSectionsToRender() {
      return [{ id: 'spruce-cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      setTimeout(() => {
        this.classList.add('animate', 'active');
        const panel = this.querySelector('.spc__panel');
        if (panel && typeof trapFocus === 'function') trapFocus(panel, panel);
      });
      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    // Called by product-form.js after an add-to-cart with the sections it requested.
    renderContents(parsedState) {
      if (parsedState && parsedState.sections) this.applySections(parsedState.sections);
      this.open();
      this.scheduleRefresh(false, 0);
    }

    /* ---------- Rendering ---------- */

    applySections(sections) {
      const parser = new DOMParser();
      const drawerHtml = sections['spruce-cart-drawer'];
      if (drawerHtml) {
        const doc = parser.parseFromString(drawerHtml, 'text/html');
        const next = doc.querySelector('#CartDrawer');
        const current = this.querySelector('#CartDrawer');
        if (next && current) {
          const scrollTop = this.querySelector('.spc__body')?.scrollTop || 0;
          current.innerHTML = next.innerHTML;
          const body = this.querySelector('.spc__body');
          if (body) body.scrollTop = scrollTop;
        }
        const nextRoot = doc.querySelector('cart-drawer');
        if (nextRoot) this.classList.toggle('is-empty', nextRoot.classList.contains('is-empty'));
      }
      const bubbleHtml = sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        const section = parser.parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
        if (section) bubble.innerHTML = section.innerHTML;
      }
    }

    async fetchSections(retries = 1) {
      const res = await nativeFetch(`${ROOT}?sections=${SECTIONS.join(',')}`, { headers: { Accept: 'application/json' } });
      if (!res.ok && retries > 0) return this.fetchSections(retries - 1);
      return res.json();
    }

    // Re-render, then fix gift lines (and re-render once more if gifts changed).
    refresh({ open = false } = {}) {
      return this.enqueue(async () => {
        this.applySections(await this.fetchSections());
        if (await this.syncGifts()) this.applySections(await this.fetchSections());
        if (open) this.open();
      });
    }

    scheduleRefresh(open = false, delay = 150) {
      this.pendingOpen = this.pendingOpen || open;
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        const shouldOpen = this.pendingOpen;
        this.pendingOpen = false;
        this.refresh({ open: shouldOpen });
      }, delay);
    }

    // Serialise cart operations so rapid clicks can't race each other.
    enqueue(task) {
      this.queue = this.queue
        .then(async () => {
          this.classList.add('is-loading');
          try {
            await task();
          } finally {
            this.classList.remove('is-loading');
          }
        })
        .catch((err) => {
          console.error('[spruce-cart]', err);
          this.showError(err.message);
        });
      return this.queue;
    }

    showError(message) {
      const body = this.querySelector('.spc__body');
      if (!body || !message) return;
      body.querySelector('.spc__error')?.remove();
      body.insertAdjacentHTML('afterbegin', '<p class="spc__error" role="alert"></p>');
      body.querySelector('.spc__error').textContent = message;
    }

    /* ---------- Free gift sync ---------- */

    readState() {
      const el = this.querySelector('[data-spc-state]');
      if (!el) return null;
      try {
        const state = JSON.parse(el.textContent);
        state.giftLines = (state.gifts || '')
          .split(',')
          .filter(Boolean)
          .map((entry) => {
            const [key, slot, qty, variant] = entry.split('|');
            return { key, slot: parseInt(slot, 10), qty: parseInt(qty, 10), variant: parseInt(variant, 10) };
          });
        return state;
      } catch (e) {
        return null;
      }
    }

    // Returns true when the cart was modified.
    async syncGifts() {
      const state = this.readState();
      if (!state) return false;

      const target = state.free;
      const updates = {};
      const kept = new Set();

      state.giftLines.forEach((line) => {
        if (!(line.slot >= 1 && line.slot <= target) || kept.has(line.slot)) {
          updates[line.key] = 0;
        } else {
          kept.add(line.slot);
          if (line.qty !== 1) updates[line.key] = 1;
        }
      });

      const adds = [];
      if (state.defaultGiftVariant) {
        for (let slot = 1; slot <= target; slot++) {
          if (!kept.has(slot)) {
            adds.push({ id: state.defaultGiftVariant, quantity: 1, properties: { _spruce_gift: String(slot) } });
          }
        }
      }

      let changed = false;
      if (Object.keys(updates).length) {
        await postJSON('cart/update.js', { updates });
        changed = true;
      }
      if (adds.length) {
        try {
          await postJSON('cart/add.js', { items: adds });
          changed = true;
        } catch (err) {
          console.warn('[spruce-cart] could not add free gift:', err.message);
        }
      }
      return changed;
    }

    /* ---------- User actions ---------- */

    onClick(event) {
      const target = event.target;
      if (target.closest('[data-spc-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const remove = target.closest('[data-spc-remove]');
      if (remove) return this.changeLine(remove.dataset.spcRemove, 0);

      const qty = target.closest('[data-spc-qty]');
      if (qty) return this.changeLine(qty.dataset.spcQty, parseInt(qty.dataset.qty, 10));

      const add = target.closest('[data-spc-add]');
      if (add) {
        const item = { id: parseInt(add.dataset.spcAdd, 10), quantity: 1 };
        if (add.dataset.plan) item.selling_plan = parseInt(add.dataset.plan, 10);
        this.enqueue(() => postJSON('cart/add.js', { items: [item] })).then(() => this.refresh());
      }
    }

    onChange(event) {
      const select = event.target.closest('[data-spc-gift]');
      if (!select) return;
      const key = select.dataset.spcGift;
      const slot = select.dataset.slot;
      const variant = parseInt(select.value, 10);
      this.enqueue(async () => {
        await postJSON('cart/update.js', { updates: { [key]: 0 } });
        await postJSON('cart/add.js', {
          items: [{ id: variant, quantity: 1, properties: { _spruce_gift: String(slot) } }],
        });
      }).then(() => this.refresh());
    }

    changeLine(key, quantity) {
      this.enqueue(() => postJSON('cart/change.js', { id: key, quantity: Math.max(0, quantity) })).then(() =>
        this.refresh()
      );
    }

    /* ---------- Cart changes made by other scripts (apps, page builders, /cart page) ---------- */

    interceptCartRequests() {
      const drawer = this;
      const notify = (url) => {
        if (CART_URL_RE.test(url)) drawer.scheduleRefresh(/\/cart\/add/.test(url));
      };

      window.fetch = function (...args) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        return nativeFetch(...args).then((res) => {
          if (res.ok) notify(url);
          return res;
        });
      };

      const open = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.addEventListener('load', () => this.status < 400 && notify(String(url)));
        return open.call(this, method, url, ...rest);
      };
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  // Replo pages (header cart icon, "Add to Cart" buttons) run `Rebuy.SmartCart.show()`, left over from
  // when Rebuy was the cart. Route those calls to this drawer instead. When Rebuy is also installed
  // (SPRUCE_FORCE_DRAWER, set on the B3G1 page), keep re-applying the override after Rebuy loads.
  const drawer = () => document.querySelector('cart-drawer');
  const smartCart = {
    show: () => drawer()?.open(),
    hide: () => drawer()?.close(),
    toggle: () => {
      const d = drawer();
      if (d) d.classList.contains('active') ? d.close() : d.open();
    },
  };
  const applyShim = () => {
    window.Rebuy = window.Rebuy || {};
    window.Rebuy.SmartCart = Object.assign(window.Rebuy.SmartCart || {}, smartCart);
  };
  if (!window.Rebuy) applyShim();
  if (window.SPRUCE_FORCE_DRAWER) {
    applyShim();
    let tries = 0;
    const timer = setInterval(() => {
      applyShim();
      if (++tries > 40) clearInterval(timer);
    }, 500);
  }
})();
